import { useEffect, useRef, useState } from "react";
import { Phone } from "lucide-react";
import api from "@/lib/api";

/**
 * FloatingCallButton
 *
 * A draggable circular "tap-to-call" button pinned above the mobile bottom nav
 * (bottom-left by default). Position is persisted per browser via localStorage
 * so it stays wherever the user parks it. Uses `tel:` so tapping fires the
 * device's dialler with the admin-configured business number.
 *
 * The phone number and enable flag come from `/settings/social` — fully
 * editable in Admin → Social tab (`call_phone`, `call_enabled`).
 *
 * We distinguish drag vs tap by a 6 px movement threshold so a quick tap
 * still triggers the call; a longer drag repositions the button.
 */
const STORAGE_KEY = "tnt_call_btn_pos_v1";
const SIZE = 56;                        // px — matches w-14 h-14
const MOBILE_NAV_H = 64;                // Layout's `mobile-bottom-nav` height
const EDGE = 16;                        // safe gap from viewport edge

export default function FloatingCallButton() {
  const [cfg, setCfg] = useState({ call_enabled: true, call_phone: "" });
  const [pos, setPos] = useState(null); // {left, top} in px
  const draggingRef = useRef(null);     // { startX, startY, origLeft, origTop, moved }
  const btnRef = useRef(null);

  // Load config once. If the phone is empty we simply don't render.
  useEffect(() => {
    api.get("/settings/social").then((r) => setCfg({
      call_enabled: r.data?.call_enabled ?? true,
      call_phone: r.data?.call_phone || "",
    })).catch(() => {});
  }, []);

  // Compute the initial position AFTER we know the viewport size:
  //   - restore saved position if present and still on-screen
  //   - otherwise bottom-left, above the mobile nav on small screens
  useEffect(() => {
    const compute = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const isMobile = vw < 1024;                    // matches lg: breakpoint
      const bottomOffset = (isMobile ? MOBILE_NAV_H : 0) + EDGE;
      let next = { left: EDGE, top: vh - SIZE - bottomOffset };
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
        if (saved && typeof saved.left === "number" && typeof saved.top === "number") {
          // Clamp saved position to current viewport so a resize doesn't hide it.
          next = {
            left: Math.max(EDGE, Math.min(vw - SIZE - EDGE, saved.left)),
            top: Math.max(EDGE, Math.min(vh - SIZE - bottomOffset, saved.top)),
          };
        }
      } catch { /* noop */ }
      setPos(next);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const onPointerDown = (e) => {
    if (!btnRef.current) return;
    btnRef.current.setPointerCapture?.(e.pointerId);
    draggingRef.current = {
      startX: e.clientX, startY: e.clientY,
      origLeft: pos?.left ?? EDGE, origTop: pos?.top ?? EDGE,
      moved: false,
    };
  };
  const onPointerMove = (e) => {
    const d = draggingRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < 6) return;  // still counts as a tap
    d.moved = true;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const isMobile = vw < 1024;
    const bottomOffset = (isMobile ? MOBILE_NAV_H : 0) + EDGE;
    const nextLeft = Math.max(EDGE, Math.min(vw - SIZE - EDGE, d.origLeft + dx));
    const nextTop  = Math.max(EDGE, Math.min(vh - SIZE - bottomOffset, d.origTop + dy));
    setPos({ left: nextLeft, top: nextTop });
  };
  const onPointerUp = (e) => {
    const d = draggingRef.current;
    draggingRef.current = null;
    if (!d) return;
    if (d.moved && pos) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pos)); } catch { /* noop */ }
      return;   // don't fire the call when the user was dragging
    }
    // Treat as a tap → dial the business number.
    if (cfg.call_phone) {
      window.location.href = `tel:${cfg.call_phone.replace(/[^\d+]/g, "")}`;
    }
  };

  if (!cfg.call_enabled || !cfg.call_phone || !pos) return null;
  const isMobileView = typeof window !== "undefined" && window.innerWidth < 1024;
  const stickyCta = typeof document !== "undefined" && document.body.classList.contains("has-sticky-cta");
  const tel = `tel:${cfg.call_phone.replace(/[^\d+]/g, "")}`;

  // Mobile: docked bottom-right just above the bottom nav (never floats over content / not draggable).
  if (isMobileView) {
    return (
      <a
        href={tel}
        data-testid="floating-call-btn"
        aria-label={`Call ${cfg.call_phone}`}
        style={{ bottom: MOBILE_NAV_H + 10 + (stickyCta ? 72 : 0), right: 12 }}
        className="fixed z-[45] w-11 h-11 rounded-full bg-emerald-500 active:bg-emerald-600 text-white shadow-[0_8px_20px_-4px_rgba(16,185,129,0.5)] flex items-center justify-center"
      >
        <Phone className="w-5 h-5" />
      </a>
    );
  }

  return (
    <button
      ref={btnRef}
      type="button"
      data-testid="floating-call-btn"
      aria-label={`Call ${cfg.call_phone}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => { draggingRef.current = null; }}
      style={{ left: pos.left, top: pos.top, touchAction: "none" }}
      className="fixed z-[60] w-14 h-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_10px_30px_-4px_rgba(16,185,129,0.55)] flex items-center justify-center cursor-grab active:cursor-grabbing transition-colors"
    >
      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-40 animate-ping" aria-hidden="true" />
      <Phone className="w-6 h-6 relative" />
    </button>
  );
}
